import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Collapse,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { askrClients, PRODUCTION_SEGMENTS_QUERY } from "providers/AskrProvider";
import {
  midgardClients,
  DATA_DUMP_AGGREGATE_QUERY,
} from "providers/MidgardProvider";
import { MACHEMETRY_URL } from "providers/MachemetryProvider";
import { useVisibleProcesses } from "utils/customHooks/genericHooks";
import { ONE_SECOND_OF_MS } from "utils/timeUtils";
import {
  ArrowBackIos,
  CancelOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";

// Service status types
type ServiceStatus = "operational" | "degraded" | "outage" | "checking";

interface ServiceDetail {
  timestamp: Date;
  message: string;
  responseTime?: number;
}

interface ServiceInfo {
  id: string;
  name: string;
  description: string;
  status: ServiceStatus;
  detail?: ServiceDetail;
  expanded?: boolean;
}

const StatusPage = styled(Container)`
  padding-top: 2rem;
  padding-bottom: 4rem;
  max-width: 1000px;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const ServicesContainer = styled(Paper)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);
  * {
    font-family: "Roboto";
  }
`;

const ServicesList = styled.div`
  width: 100%;
`;

const ServiceItem = styled.div`
  padding: 0;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ServiceItemHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  user-select: none;
  justify-content: space-between;
`;

const ServiceItemDetails = styled.div`
  padding: 0.5rem 1.5rem 1.5rem;
  margin-top: -0.75rem;
`;

const DetailTimestamp = styled.div`
  color: #6b7280;
  font-size: 0.8125rem;
`;

const DetailMessage = styled.div`
  font-weight: 600;
  color: #1f2937;
  flex: 1;
  margin-bottom: 8px;
`;

const DetailStatusRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 0.25rem;
  .status-icon {
    margin-right: 0.25rem;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.div`
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ServiceDescription = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 150px;
`;

const StatusWithIcon = styled.div`
  display: flex;
  align-items: center;
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 24px;
  color: #6b7280;

  .MuiSvgIcon-root {
    font-size: 24px;
  }
`;

const StatusText = styled.span`
  margin-left: 0.5rem;
  font-weight: 500;
`;

const ServiceStatusPage: React.FC = () => {
  const { translate } = useFlightTranslation();
  const ckey = useCustomerKey();
  const processes = useVisibleProcesses();
  const [services, setServices] = useState<ServiceInfo[]>([
    {
      id: "flight-app",
      name: "Flight",
      description: "Main application interface",
      status: "checking",
      expanded: false,
    },
    {
      id: "midgard",
      name: "Midgard",
      description: "Aggregate data server",
      status: "checking",
      expanded: false,
    },
    {
      id: "askr",
      name: "Askr",
      description: "Timeline segments data server",
      status: "checking",
      expanded: false,
    },
    {
      id: "machemetry",
      name: "Machemetry",
      description: "Timeline ticks data server",
      status: "checking",
      expanded: false,
    },
  ]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Check Flight app status by making a request to the Flight app URL
  const checkFlightStatus = useCallback(async (): Promise<{
    status: ServiceStatus;
    detail: ServiceDetail;
  }> => {
    try {
      const startTime = Date.now();
      // Using AbortController to implement timeout for fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        const response = await fetch("https://flight.raven.ai", {
          method: "GET",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        if (response.ok) {
          return {
            status: "operational",
            detail: {
              timestamp: new Date(),
              message: "The service is up and running with no known issues.",
              responseTime,
            },
          };
        } else {
          return {
            status: "degraded",
            detail: {
              timestamp: new Date(),
              message: `Service responded with status: ${response.status} ${response.statusText}`,
              responseTime,
            },
          };
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        // If we get a CORS error, it means the server is up but blocking cross-origin requests
        // This is expected when running from localhost, so treat it as operational
        if (
          fetchError.name === "TypeError" ||
          (fetchError.message && fetchError.message.includes("cors")) ||
          fetchError.name === "AbortError"
        ) {
          console.log(
            "CORS error when checking flight.raven.ai - treating as operational"
          );
          return {
            status: "operational",
            detail: {
              timestamp: new Date(),
              message:
                "CORS error detected but service appears to be operational. This is expected when running from localhost.",
              responseTime,
            },
          };
        }

        // For other errors, log and return outage
        throw fetchError;
      }
    } catch (error: any) {
      console.error("Error checking Flight status:", error);
      return {
        status: "outage",
        detail: {
          timestamp: new Date(),
          message: `Service is experiencing an outage: ${error.message || "Unknown error"}`,
        },
      };
    }
  }, []);

  // Check Askr status by making a small GraphQL query
  const checkAskrStatus = useCallback(async (): Promise<{
    status: ServiceStatus;
    detail: ServiceDetail;
  }> => {
    try {
      if (!ckey || !processes || processes.length === 0) {
        return {
          status: "degraded",
          detail: {
            timestamp: new Date(),
            message: "Customer key or process data is not available.",
          },
        };
      }

      const startTime = Date.now();
      const processId = processes[0].id;
      const now = Date.now();
      const ge = now - ONE_SECOND_OF_MS;
      const lt = now;

      const askrClient = askrClients.get(ckey);
      const response = await askrClient.query({
        query: PRODUCTION_SEGMENTS_QUERY,
        variables: {
          machemetrySourceName: processId,
          ge: ge.toString(),
          lt: lt.toString(),
        },
        fetchPolicy: "network-only",
      });

      const responseTime = Date.now() - startTime;

      if (response.data) {
        return {
          status: "operational",
          detail: {
            timestamp: new Date(),
            message:
              "The timeline server is responding to queries successfully.",
            responseTime,
          },
        };
      } else {
        return {
          status: "degraded",
          detail: {
            timestamp: new Date(),
            message: "The timeline server responded, but with empty data.",
            responseTime,
          },
        };
      }
    } catch (error: any) {
      console.error("Error checking Askr status:", error);
      return {
        status: "outage",
        detail: {
          timestamp: new Date(),
          message: `Timeline server is experiencing an outage: ${error.message || "Unknown error"}`,
        },
      };
    }
  }, [ckey, processes]);

  // Check Midgard status by making a small GraphQL query
  const checkMidgardStatus = useCallback(async (): Promise<{
    status: ServiceStatus;
    detail: ServiceDetail;
  }> => {
    try {
      if (!ckey || !processes || processes.length === 0) {
        return {
          status: "degraded",
          detail: {
            timestamp: new Date(),
            message: "Customer key or process data is not available.",
          },
        };
      }

      const startTime = Date.now();
      const processId = processes[0].id;
      const now = Date.now();
      const ge = now - ONE_SECOND_OF_MS;
      const lt = now;

      const midgardClient = midgardClients.get(ckey);
      const response = await midgardClient.query({
        query: DATA_DUMP_AGGREGATE_QUERY,
        variables: {
          ge: ge.toString(),
          lt: lt.toString(),
          machemetryIds: [processId],
          shouldAllocate: false,
          useCache: false,
        },
        fetchPolicy: "network-only",
      });

      const responseTime = Date.now() - startTime;

      if (response.data) {
        return {
          status: "operational",
          detail: {
            timestamp: new Date(),
            message:
              "The aggregate data server is responding to queries successfully.",
            responseTime,
          },
        };
      } else {
        return {
          status: "degraded",
          detail: {
            timestamp: new Date(),
            message:
              "The aggregate data server responded, but with empty data.",
            responseTime,
          },
        };
      }
    } catch (error: any) {
      console.error("Error checking Midgard status:", error);
      return {
        status: "outage",
        detail: {
          timestamp: new Date(),
          message: `Aggregate data server is experiencing an outage: ${error.message || "Unknown error"}`,
        },
      };
    }
  }, [ckey, processes]);

  // Check Machemetry status by making a ping request
  const checkMachemetryStatus = useCallback(async (): Promise<{
    status: ServiceStatus;
    detail: ServiceDetail;
  }> => {
    try {
      // We'll just check if the Machemetry API is responding
      const startTime = Date.now();
      // Using AbortController to implement timeout for fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        // Try to fetch the API root (or could be replaced with a specific health endpoint if available)
        const response = await fetch(MACHEMETRY_URL, {
          method: "GET",
          signal: controller.signal,
          headers: {
            // Just a probe request, the server might reject it due to authorization
            // but that's fine - we just want to know if it's responding
          },
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        if (response.ok) {
          return {
            status: "operational",
            detail: {
              timestamp: new Date(),
              message: "The ticks data server is responding successfully.",
              responseTime,
            },
          };
        } else {
          return {
            status: "degraded",
            detail: {
              timestamp: new Date(),
              message: `Ticks data server responded with status: ${response.status} ${response.statusText}`,
              responseTime,
            },
          };
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        // If we get a CORS error, it means the server is up but blocking cross-origin requests
        // This is expected when running from localhost, so treat it as operational
        if (
          fetchError.name === "TypeError" ||
          (fetchError.message && fetchError.message.includes("cors")) ||
          fetchError.name === "AbortError"
        ) {
          console.log(
            "CORS or auth error when checking Machemetry - treating as operational"
          );
          return {
            status: "operational",
            detail: {
              timestamp: new Date(),
              message:
                "CORS error detected but ticks data server appears to be operational. This is expected when running from localhost.",
              responseTime,
            },
          };
        }

        // For other errors, log and return outage
        throw fetchError;
      }
    } catch (error: any) {
      console.error("Error checking Machemetry status:", error);
      return {
        status: "outage",
        detail: {
          timestamp: new Date(),
          message: `Ticks data server is experiencing an outage: ${error.message || "Unknown error"}`,
        },
      };
    }
  }, []);

  // Check all services
  const checkServiceStatus = useCallback(async () => {
    // Only check services if we have both ckey and processes
    if (!ckey || !processes || processes.length === 0) {
      return;
    }

    // Set all services to checking state
    setServices(prevServices =>
      prevServices.map(service => ({ ...service, status: "checking" }))
    );

    // Check each service in parallel
    const [flightResult, askrResult, midgardResult, machemetryResult] =
      await Promise.all([
        checkFlightStatus(),
        checkAskrStatus(),
        checkMidgardStatus(),
        checkMachemetryStatus(),
      ]);

    // Update services with their statuses and details
    setServices(prevServices => [
      {
        ...prevServices[0],
        status: flightResult.status,
        detail: flightResult.detail,
      },
      {
        ...prevServices[1],
        status: askrResult.status,
        detail: askrResult.detail,
      },
      {
        ...prevServices[2],
        status: midgardResult.status,
        detail: midgardResult.detail,
      },
      {
        ...prevServices[3],
        status: machemetryResult.status,
        detail: machemetryResult.detail,
      },
    ]);

    setLastUpdated(new Date());
  }, [
    ckey,
    processes,
    checkFlightStatus,
    checkAskrStatus,
    checkMidgardStatus,
    checkMachemetryStatus,
  ]);

  // Toggle the expanded state of a service
  const toggleServiceExpanded = useCallback((serviceId: string) => {
    setServices(prevServices =>
      prevServices.map(service =>
        service.id === serviceId
          ? { ...service, expanded: !service.expanded }
          : service
      )
    );
  }, []);

  useEffect(() => {
    // Check status immediately on load, but only if we have ckey and processes
    if (ckey && processes && processes.length > 0) {
      checkServiceStatus();

      // Check status every 60 seconds
      const intervalId = setInterval(() => {
        checkServiceStatus();
      }, 60 * 1000);

      // Clear interval on unmount
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
    return () => {};
  }, [ckey, processes, checkServiceStatus]);

  const getStatusIcon = (status: ServiceStatus, isOutline?: boolean) => {
    if (status === "checking") {
      return <CircularProgress size={24} />;
    } else if (status === "operational") {
      return isOutline ? (
        <CheckCircleOutline style={{ color: "#4caf50", fontSize: "24px" }} />
      ) : (
        <CheckCircleIcon style={{ color: "#4caf50", fontSize: "24px" }} />
      );
    } else if (status === "degraded") {
      return isOutline ? (
        <CancelOutlined style={{ color: "#ff9800", fontSize: "24px" }} />
      ) : (
        <CancelIcon style={{ color: "#ff9800", fontSize: "24px" }} />
      );
    } else {
      return isOutline ? (
        <CancelOutlined style={{ color: "#f44336", fontSize: "24px" }} />
      ) : (
        <CancelIcon style={{ color: "#f44336", fontSize: "24px" }} />
      );
    }
  };

  const getStatusText = (status: ServiceStatus) => {
    if (status === "checking") {
      return translate("Checking status");
    } else if (status === "operational") {
      return translate("No known issues");
    } else if (status === "degraded") {
      return translate("Degraded performance");
    } else {
      return translate("Service disruption");
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatResponseTime = (ms?: number) => {
    if (ms === undefined) {
      return "";
    }
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
  };

  const formattedDate = lastUpdated.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });

  return (
    <StatusPage maxWidth={false}>
      <HeaderSection>
        <Typography variant="h4" component="h1" gutterBottom>
          {translate("Status and outages of Raven services")}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {translate("Updated")} {formattedDate}
        </Typography>
      </HeaderSection>

      <ServicesContainer>
        <ServicesList>
          {services.map(service => (
            <ServiceItem
              key={service.id}
              onClick={() => toggleServiceExpanded(service.id)}
              style={{ cursor: "pointer" }}
            >
              <ServiceItemHeader>
                <ServiceInfo>
                  <ServiceName>{service.name}</ServiceName>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </ServiceInfo>
                <StatusInfo>
                  <StatusWithIcon>
                    {getStatusIcon(service.status)}
                    <StatusText>{getStatusText(service.status)}</StatusText>
                  </StatusWithIcon>
                  <ExpandIcon>
                    <ArrowBackIos
                      style={{
                        transition: "transform 0.3s ease-in-out",
                        transform: `scale(0.8) translateY(${service.expanded ? 6 : -4}px) rotate(${service.expanded ? 90 : 270}deg)`,
                      }}
                    />
                  </ExpandIcon>
                </StatusInfo>
              </ServiceItemHeader>

              <Collapse in={service.expanded} timeout={300} unmountOnExit>
                {service.detail && (
                  <ServiceItemDetails>
                    <DetailStatusRow>
                      <div className="status-icon">
                        {getStatusIcon(service.status, true)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <DetailMessage>{service.detail.message}</DetailMessage>
                        <DetailTimestamp>
                          {formatDate(service.detail.timestamp)}
                          {service.detail.responseTime !== undefined && (
                            <span style={{ marginLeft: "8px" }}>
                              Response time:{" "}
                              {formatResponseTime(service.detail.responseTime)}
                            </span>
                          )}
                        </DetailTimestamp>
                      </div>
                    </DetailStatusRow>
                  </ServiceItemDetails>
                )}
              </Collapse>
            </ServiceItem>
          ))}
        </ServicesList>
      </ServicesContainer>
    </StatusPage>
  );
};

export default ServiceStatusPage;
